import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { ExtractedPolicy, FieldMetadata } from "@/types/extraction";

export interface ExtractionDraftContext {
  storagePath: string;
  extractedPolicy: ExtractedPolicy;
  metadata: FieldMetadata;
}

/**
 * Saves and restores form drafts to Supabase.
 *
 * Usage:
 *   const { saveDraft, loadDraft, clearDraft, lastSaved, isSaving } = useDraftPersistence();
 *
 *   // On step transition or auto-save interval:
 *   await saveDraft(currentStep, formData, extractionContext);
 *
 *   // On initial load:
 *   const draft = await loadDraft();
 *   if (draft) { restoreFormState(draft.form_data, draft.current_step); }
 *
 *   // On final submission:
 *   await clearDraft();
 */
export function useDraftPersistence() {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const saveDraft = useCallback(
    async (
      currentStep: number,
      formData: Record<string, unknown>,
      extractionContext?: ExtractionDraftContext,
    ) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      setIsSaving(true);
      const { error } = await supabase.from("drafts").upsert(
        {
          user_id: user.id,
          current_step: currentStep,
          form_data: formData,
          extraction_context: extractionContext ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

      setIsSaving(false);

      if (!error) {
        setLastSaved(new Date());
        // Also save to localStorage as backup
        try {
          localStorage.setItem(
            "onecalla_draft_backup",
            JSON.stringify({
              currentStep,
              formData,
              extractionContext: extractionContext ?? null,
              savedAt: new Date().toISOString(),
            })
          );
        } catch {
          // localStorage may be unavailable, that is fine
        }
      }

      return { error };
    },
    []
  );

  const loadDraft = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from("drafts")
      .select("*")
      .eq("user_id", user.id)
      .single();

    if (error || !data) {
      // Try localStorage backup
      try {
        const backup = localStorage.getItem("onecalla_draft_backup");
        if (backup) return JSON.parse(backup);
      } catch {
        // ignore
      }
      return null;
    }

    return data;
  }, []);

  const clearDraft = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from("drafts").delete().eq("user_id", user.id);
    try {
      localStorage.removeItem("onecalla_draft_backup");
    } catch {
      // ignore
    }
  }, []);

  return {
    saveDraft,
    loadDraft,
    clearDraft,
    lastSaved,
    isSaving,
  };
}
